import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { deltaPct, fmtMin, fmtNum, fmtPct } from './format';
import { Tile, alert, card, cardHead, grid, muted, td, tdR, th, thR } from './Overview';

// "Capacity" (spec 7.8): waterfall from potential to net production that closes to the
// kilogram, utilisation per line, loss breakdown table.

interface Cap { availMin: number; prodMin: number; potentialKg: number; expectedKg: number; grossKg: number; netKg: number; source: 'winder' | 'spools'; utilization: number | null; loss: { downtime: number; shortStops: number; speed: number; scrap: number } }
interface Data { total: Cap & { coverage: number | null; availAllMin: number }; previous: (Cap & { coverage: number | null }) | null; byLine: (Cap & { id: number; label: string })[]; byMaterial: (Cap & { id: number; label: string })[] }

const Waterfall: React.FC<{ steps: { label: string; value: number; kind: 'total' | 'loss' | 'result' }[] }> = ({ steps }) => {
  const width = 760, height = 260, padL = 50, padB = 40, padT = 16;
  const innerW = width - padL - 12, innerH = height - padT - padB;
  const max = Math.max(1, ...steps.map(s => s.value));
  const slot = innerW / steps.length, barW = Math.min(90, slot * 0.6);
  const y = (v: number) => padT + innerH - (v / max) * innerH;
  let running = 0;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto' }}>
      {[0, 0.5, 1].map(f => <g key={f}><line x1={padL} x2={width - 12} y1={y(f * max)} y2={y(f * max)} stroke="#e2e8f0" /><text x={padL - 6} y={y(f * max) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{fmtNum(f * max, 0)}</text></g>)}
      {steps.map((s, i) => {
        const x = padL + i * slot + (slot - barW) / 2;
        let top: number, bottom: number, color: string;
        if (s.kind === 'total') { running = s.value; top = s.value; bottom = 0; color = '#667eea'; }
        else if (s.kind === 'loss') { top = running; running = Math.max(0, running - s.value); bottom = running; color = s.value < 0 ? '#0f9d58' : '#e8590c'; }
        else { top = s.value; bottom = 0; color = '#2f80ed'; }
        const hi = Math.max(top, bottom), lo = Math.min(top, bottom);
        return (
          <g key={s.label}>
            <rect x={x} y={y(hi)} width={barW} height={Math.max(1, y(lo) - y(hi))} rx={3} fill={color}><title>{`${s.label}: ${fmtNum(s.value, 1)} kg`}</title></rect>
            <text x={x + barW / 2} y={y(hi) - 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e293b">{s.kind === 'loss' ? `−${fmtNum(s.value, 0)}` : fmtNum(s.value, 0)}</text>
            <text x={x + barW / 2} y={height - 22} textAnchor="middle" fontSize="10" fill="#64748b">{s.label}</text>
            {i < steps.length - 1 && <line x1={x + barW} x2={x + slot + (slot - barW) / 2} y1={y(s.kind === 'loss' ? running : s.value)} y2={y(s.kind === 'loss' ? running : s.value)} stroke="#cbd5e1" strokeDasharray="3 3" />}
          </g>
        );
      })}
    </svg>
  );
};

const Capacity: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/analytics/capacity', { params: toQuery(filters) })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const tot = data?.total;
  const p = data?.previous ?? null;
  const d = (cur: number | null | undefined, prev: number | null | undefined) => (filters.compare && p ? deltaPct(cur, prev) : null);
  const check = tot ? Math.round((tot.potentialKg - tot.loss.downtime - tot.loss.shortStops - tot.loss.speed - tot.loss.scrap - tot.netKg) * 10) / 10 : 0;

  const LossRow: React.FC<{ r: Cap & { label: string } }> = ({ r }) => (
    <tr>
      <td style={{ ...td, fontWeight: 700 }}>{r.label}</td>
      <td style={tdR}>{fmtMin(r.availMin)}</td>
      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(r.potentialKg, 1)}</td>
      <td style={{ ...tdR, color: '#e8590c' }}>{fmtNum(r.loss.downtime, 1)}</td>
      <td style={{ ...tdR, color: '#e8590c' }}>{fmtNum(r.loss.shortStops, 1)}</td>
      <td style={{ ...tdR, color: r.loss.speed < 0 ? '#0f9d58' : '#e8590c' }}>{fmtNum(r.loss.speed, 1)}</td>
      <td style={{ ...tdR, color: '#e8590c' }}>{fmtNum(r.loss.scrap, 1)}</td>
      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(r.netKg, 1)} <span style={muted}>{r.source === 'winder' ? t('capacity.srcWinder') : t('capacity.srcSpools')}</span></td>
      <td style={{ ...tdR, fontWeight: 700 }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 60, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', display: 'inline-block' }}><span style={{ display: 'block', width: `${Math.min(100, (r.utilization ?? 0) * 100)}%`, height: '100%', background: '#667eea' }} /></span>{fmtPct(r.utilization)}</span></td>
    </tr>
  );

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>📐 {t('capacity.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('capacity.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error && <div style={alert}>{error}</div>}
      {loading && !data && <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>{t('common.loading')}</div>}

      {tot && (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          {(tot.coverage ?? 0) < 0.999 && (
            <div style={{ ...alert, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>
              {t('capacity.coverageWarn', { pct: fmtPct(tot.coverage, 0), hours: fmtNum(tot.availAllMin / 60, 1) })}
            </div>
          )}
          <div style={grid}>
            <Tile label={t('capacity.potential')} value={`${fmtNum(tot.potentialKg, 0)} kg`} sub={t('capacity.potentialSub', { hours: fmtNum(tot.availMin / 60, 1) })} delta={d(tot.potentialKg, p?.potentialKg)} vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('capacity.net')} value={`${fmtNum(tot.netKg, 0)} kg`} sub={tot.source === 'winder' ? t('capacity.srcWinderLong') : t('capacity.srcSpoolsLong')} delta={d(tot.netKg, p?.netKg)} vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('analytics.kpiUtilization')} value={fmtPct(tot.utilization)} delta={d(tot.utilization, p?.utilization)} vsLabel={t('analytics.vsPrev')} bar={tot.utilization} />
            <Tile label={t('capacity.lossDown')} value={`${fmtNum(tot.loss.downtime + tot.loss.shortStops, 0)} kg`} sub={t('capacity.lossDownSub', { short: fmtNum(tot.loss.shortStops, 0) })} delta={d(tot.loss.downtime + tot.loss.shortStops, p ? p.loss.downtime + p.loss.shortStops : null)} invert vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('capacity.lossSpeed')} value={`${fmtNum(tot.loss.speed, 0)} kg`} sub={t('capacity.lossSpeedSub')} delta={d(tot.loss.speed, p?.loss.speed)} invert vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('capacity.lossScrap')} value={`${fmtNum(tot.loss.scrap, 0)} kg`} sub={t('capacity.lossScrapSub')} delta={d(tot.loss.scrap, p?.loss.scrap)} invert vsLabel={t('analytics.vsPrev')} />
          </div>

          <div style={card}>
            <div style={cardHead}>
              <b>{t('capacity.waterfall')}</b>
              <span style={{ ...muted, color: Math.abs(check) < 0.15 ? '#059669' : '#dc2626', fontWeight: 700 }}>{Math.abs(check) < 0.15 ? t('capacity.closes') : t('capacity.notCloses', { diff: fmtNum(check, 1) })}</span>
            </div>
            <Waterfall steps={[
              { label: t('capacity.potential'), value: tot.potentialKg, kind: 'total' },
              { label: t('capacity.wDown'), value: tot.loss.downtime, kind: 'loss' },
              { label: t('capacity.wShort'), value: tot.loss.shortStops, kind: 'loss' },
              { label: t('capacity.wSpeed'), value: tot.loss.speed, kind: 'loss' },
              { label: t('capacity.wScrap'), value: tot.loss.scrap, kind: 'loss' },
              { label: t('capacity.net'), value: tot.netKg, kind: 'result' },
            ]} />
            <div style={{ ...muted, marginTop: 6 }}>{t('capacity.formula')}</div>
          </div>

          {[['byLine', t('capacity.byLine')], ['byMaterial', t('capacity.byMaterial')]].map(([key, title]) => (
            <div key={key} style={card}>
              <div style={cardHead}><b>{title}</b><span style={muted}>{t('capacity.tableSub')}</span></div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ background: '#f8f9fa' }}>
                    <th style={th}>{key === 'byLine' ? t('analytics.colLine') : t('standards.colMaterial')}</th><th style={thR}>{t('downtime.kpiAvail')}</th><th style={thR}>{t('capacity.potential')}</th><th style={thR}>{t('capacity.wDown')}</th><th style={thR}>{t('capacity.wShort')}</th><th style={thR}>{t('capacity.wSpeed')}</th><th style={thR}>{t('capacity.wScrap')}</th><th style={thR}>{t('capacity.net')}</th><th style={thR}>{t('analytics.kpiUtilization')}</th>
                  </tr></thead>
                  <tbody>
                    {(data![key as 'byLine' | 'byMaterial']).length === 0 ? <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 20 }}>{t('capacity.empty')}</td></tr>
                      : (data![key as 'byLine' | 'byMaterial']).map(r => <LossRow key={r.id} r={r} />)}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Capacity;
